import { useState } from 'react';

import { useConfig, useHealth } from '../api/hooks';
import { SOURCE_LABELS } from '../api/types';
import type { Mode } from '../api/types';
import { Role } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { Badge } from '../components/Badge';
import type { Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { Card, CardHeader, StatCard } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { EmptyState, QueryBoundary } from '../components/states';
import { ModeBadge } from '../components/DomainBadges';
import { useFormatters } from '../time/format';
import { formatDuration } from '../time/useNow';
import { LogConsole } from './partials/LogConsole';
import { RunHistory } from './partials/RunHistory';
import { SourcePulse } from './partials/SourcePulse';

const SECRET_TONES: Record<string, Tone> = {
  set: 'success',
  placeholder: 'danger',
  unknown: 'warning',
};

/**
 * The operations view: is intake running, what has it been doing, and what did it just say.
 *
 * Structured as those three questions in that order, because the page has to work for someone
 * who arrived at it after an alarm and does not yet know which source is at fault. A grid of
 * countdown rings answers the first without reading anything; the run history answers the
 * second; the log console answers the third, which previously required an AWS console login
 * that most of the people who need it do not have.
 *
 * Selecting a source card narrows the console and the history to it, so the path from "one
 * ring has stopped" to "this is the error it logged" is two clicks and no query syntax.
 *
 * Admin-only in the route table, not just here. Source cursors, secret key status and raw log
 * output are all on this page, and vendor error text is the one field nobody controls the
 * shape of.
 */
export function Health() {
  const principal = usePrincipal();
  const admin = principal.role >= Role.ADMIN;
  const health = useHealth(admin);
  const config = useConfig(admin);
  const { precise, relative } = useFormatters();
  const [selected, setSelected] = useState('');

  if (!admin) {
    return (
      <>
        <PageHeader title="Health" />
        <Card>
          <EmptyState
            title="Health requires the IRA.Admin role"
            detail="This page exposes source cursors, secret status and platform log output, so it is admin-only."
          />
        </Card>
      </>
    );
  }

  const placeholders = Object.entries(health.data?.secrets ?? {}).flatMap(([secret, keys]) =>
    Object.entries(keys)
      .filter(([, state]) => state === 'placeholder')
      .map(([key]) => `${secret}/${key}`),
  );

  const autoModes = Object.entries(config.data?.action_modes ?? {}).filter(
    ([, mode]) => mode === 'auto',
  );

  const sources = health.data?.sources ?? [];
  const pollable = sources.filter((source) => source.pollable);
  const intervalSeconds = health.data?.poll_interval_seconds ?? 180;
  // Whichever source last brought something in. The headline number on this page used to be
  // "sources reporting ok", which stays at its maximum through the failure this page exists
  // to catch: every source healthy and nothing arriving.
  const lastIngest = pollable
    .map((source) => source.last_run)
    .filter((run) => run && run.ingested > 0)
    .sort((a, b) => (b?.started_at ?? '').localeCompare(a?.started_at ?? ''))[0];

  return (
    <>
      <PageHeader
        title="Health"
        subtitle={`Intake, polling and platform logs. Sources poll every ${Math.round(intervalSeconds / 60)} minutes.${health.data?.version ? ` Version ${health.data.version}.` : ''}`}
      />

      <QueryBoundary
        isLoading={health.isPending}
        error={health.error}
        onRetry={() => void health.refetch()}
        loadingLabel="Loading health"
      >
        <div className="space-y-6">
          {/*
            A secret still holding its seeded placeholder is a deployment that looks complete
            and is not: the source will fail on its first poll. Called out at the top because
            it is the single most common cause of a silent gap in intake.
          */}
          {placeholders.length > 0 ? (
            <Callout tone="danger" role="alert">
              <p className="font-medium">
                {placeholders.length} secret {placeholders.length === 1 ? 'key' : 'keys'} still
                hold a placeholder
              </p>
              <p className="mt-0.5 opacity-90">
                These were seeded by the deploy and never populated. The sources depending on
                them cannot authenticate.
              </p>
              <ul className="mt-1 font-mono text-[11px]">
                {placeholders.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </Callout>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Sources reporting ok"
              value={`${sources.filter((s) => s.state === 'ok').length} / ${sources.length}`}
              hint={`${pollable.length} polled, ${sources.length - pollable.length} webhook-only`}
            />
            <StatCard
              label="Last alert ingested"
              value={lastIngest ? relative(lastIngest.started_at) : 'not in the last week'}
              hint={
                lastIngest
                  ? `${lastIngest.ingested} from ${SOURCE_LABELS[lastIngest.source] ?? lastIngest.source}`
                  : 'Every source can be healthy and still bring nothing in'
              }
            />
            <StatCard
              label="Automatic actions this hour"
              value={health.data?.auto_actions_last_hour ?? 0}
              hint={
                config.data
                  ? `Cap is ${config.data.auto_actions_per_hour} platform-wide`
                  : undefined
              }
              tone={
                config.data &&
                (health.data?.auto_actions_last_hour ?? 0) >= config.data.auto_actions_per_hour
                  ? 'warning'
                  : undefined
              }
            />
            <StatCard
              label="Actions set to auto"
              value={autoModes.length}
              hint={
                autoModes.length === 0
                  ? 'Everything requires approval or is disabled'
                  : autoModes.map(([name]) => name).join(', ')
              }
              tone={autoModes.length > 0 ? 'warning' : undefined}
            />
          </div>

          <section>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold text-body">Intake</h2>
              <p className="text-xs text-subtle">
                {selected
                  ? `Filtered to ${SOURCE_LABELS[selected as keyof typeof SOURCE_LABELS] ?? selected}. Click the name again to clear.`
                  : 'Click a source name to narrow the logs and history below.'}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sources.map((source) => (
                <SourcePulse
                  key={source.source}
                  source={source}
                  intervalSeconds={intervalSeconds}
                  selected={selected === source.source}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </section>

          <LogConsole
            enabled={admin}
            sources={pollable.map((source) => source.source)}
            source={selected}
            onSourceChange={setSelected}
          />

          <RunHistory enabled={admin} source={selected} />

          <Card className="p-0">
            <div className="p-5 pb-0">
              <CardHeader
                title="Cursors"
                subtitle="Where each poller got to. Advances only over alerts already archived and claimed."
              />
            </div>
            <div className="scroll-thin overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-line text-xs uppercase tracking-wide text-subtle">
                  <tr>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Source
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Last attempt
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Next poll
                    </th>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Cursor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {sources.map((source) => (
                    <tr key={source.source}>
                      <td className="px-5 py-2.5">
                        <span className="font-medium text-body">
                          {SOURCE_LABELS[source.source] ?? source.source}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                        {source.last_attempt ? (
                          <span title={precise(source.last_attempt)}>
                            {relative(source.last_attempt)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                        {source.pollable ? (
                          source.next_poll_at ? (
                            // "37 minutes ago" is a strange thing to read under "next poll",
                            // and the strangeness is the point: the schedule should have
                            // fired and did not. Labelled rather than left to be inferred.
                            <span title={precise(source.next_poll_at)}>
                              {new Date(source.next_poll_at).getTime() < Date.now() ? (
                                <span className="text-warning-fg">
                                  overdue, due {relative(source.next_poll_at)}
                                </span>
                              ) : (
                                relative(source.next_poll_at)
                              )}
                            </span>
                          ) : (
                            'unknown'
                          )
                        ) : (
                          <span className="text-subtle">not scheduled</span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 font-mono text-[11px] text-muted">
                        {source.cursor || <span className="text-subtle">never advanced</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Secrets"
              subtitle="Whether each key has been populated. Values are never returned."
            />
            {Object.keys(health.data?.secrets ?? {}).length === 0 ? (
              <EmptyState title="No secrets reported" />
            ) : (
              <div className="space-y-3">
                {Object.entries(health.data?.secrets ?? {}).map(([secret, keys]) => (
                  <div key={secret}>
                    <p className="mb-1 font-mono text-xs text-muted">{secret}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(keys).map(([key, state]) => (
                        <Badge key={key} tone={SECRET_TONES[state] ?? 'neutral'}>
                          {key}: {state}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Containment policy"
              subtitle="The deployed baseline with any runtime override applied on top."
            />
            <QueryBoundary
              isLoading={config.isPending}
              error={config.error}
              onRetry={() => void config.refetch()}
            >
              {config.data ? (
                <>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {Object.entries(config.data.action_modes)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([action, mode]) => (
                        <span
                          key={action}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-sunken px-2 py-1 text-xs"
                        >
                          <span className="font-mono text-muted">{action}</span>
                          <ModeBadge mode={mode as Mode} />
                        </span>
                      ))}
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                    <PolicyRow
                      label="Automatic actions per hour"
                      value={String(config.data.auto_actions_per_hour)}
                    />
                    <PolicyRow
                      label="Approval TTL"
                      value={`${config.data.approval_ttl_hours} hours`}
                    />
                    <PolicyRow
                      label="Complete evidence required for auto"
                      value={config.data.require_complete_evidence_for_auto ? 'yes' : 'no'}
                    />
                    <PolicyRow
                      label="Evidence token budget"
                      value={config.data.evidence_token_budget.toLocaleString()}
                    />
                    <PolicyRow
                      label="Poll cadence"
                      value={formatDuration(intervalSeconds * 1000)}
                    />
                    <PolicyRow
                      label="Protected principals"
                      value={
                        config.data.protected_principals.length === 0
                          ? 'none configured'
                          : config.data.protected_principals.join(', ')
                      }
                    />
                  </dl>
                  {config.data.protected_principals.length === 0 && autoModes.length > 0 ? (
                    <div className="mt-3">
                      <Callout tone="warning">
                        An action is set to <code className="font-mono">auto</code> with no
                        protected principals configured. Break-glass and directory-admin
                        accounts have no exclusion.
                      </Callout>
                    </div>
                  ) : null}
                </>
              ) : null}
            </QueryBoundary>
          </Card>
        </div>
      </QueryBoundary>
    </>
  );
}

function PolicyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line pb-1">
      <dt className="text-subtle">{label}</dt>
      <dd className="text-right font-medium text-body">{value}</dd>
    </div>
  );
}
